/* Votre établissement › Équipe et caisses — one page, four tabs (Scope v5 §8 · batch 22 · v3.3 migration pack equipe-qr:
   WF-031 Membres · WF-032 Invitations · WF-033 Vos caisses · WF-116 « Inviter une personne »; Défi collectif is Builder A's
   /equipe/defi, the fourth tab opens it).

   Membres: the list is @/lib/team's — added members go through `addMember`, never a copy here. A member's personal
   support is a support of genre « personnel » carrying her id, created with the shared `addSupport` exactly as the campaign's
   « Votre équipe » does, so the QR made here is the one Supports, the campaign page and her own « Mon QR » show. Search, the
   Rôle and Accès filters and « N résultats » read the list; « Voir sa carte QR » opens the support's page (WF-035, the
   manager's preview); « Gérer l’accès » opens the member's page below (the code, the QR, her numbers); the owner's row is
   « Propriétaire protégé ». « Qui peut faire quoi ? » prints the role lines of Compte et accès (ROLE_LINES), never a restatement.
   The login-less member (« Ajouter un membre », access « aucun ») stays: a prénom and a code for the till.

   Invitations: the open invitations the account carries (parametres/api `pending`, kind 'invitation') and the ones prepared
   here in the demo (this browser, per world) — Accès « Invité ». The state machine goes only as far as the app's data has
   states: an invitation is open or it is not; « renvoyer » / « révoquer » wait for the API's invitation route (TECH), so no
   button pretends. « Préparer l’invitation » with an API is disabled with the reason: nothing is sent, nothing is faked.

   No « avis » column: a member's link tap is not a review and BMR never verifies posting (Dev Notes §1). D-31: inside an
   establishment, its members only. Manager writes campaigns/gifts/roues/demandes/principale, Collaborateur none: a staff
   session reads this page, invites nobody, creates nothing.

   The members row: with an API the people and their numbers are the server's (09 §9.1). A member chooses their 4 digits at
   the till, the first time: the app never takes a code, it only says whether one is chosen and lets the owner, an admin or a
   manager « Réinitialiser son code » (POST …/pin/reset), so the member chooses again at the till (the record, V18). */
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Card, EmptyState, Field, Icon, Input, KV, List, ListRow, Modal, Note, PageHeader, Pill, Select, Skeleton, Tabs, Tile, Tiles, useToast } from '@/ui'
import { apiWrites, supportUrl, useLocations, useSupports } from '@/lib/api'
import { ALL, currentUser, useScope, type Role } from '@/lib/scope'
import { addMember, getMembers, memberPlace, resetMemberPin, worksAt, type Member } from '@/lib/team'
import { mergeSupports, type SupportRef } from '@/lib/campaign'
import { supportRefusal, useSupportWrites } from '@/lib/campaign-api'
import { currentWorld } from '@/mocks/worlds'
import { actsIn, ROLE_LABEL, ROLE_LINES, ROLE_ORDER, useBilling, type PendingRow, type TeamRole } from '@/routes/parametres/api'
import { accessLine, memberRefusal, memberRow, useMemberLedger, type MemberRow } from './membres'
import { serverLocationId } from '@/lib/mirror'
import VosCaisses from './VosCaisses'

const num = (n: number | null) => (n === null ? '—' : n.toLocaleString('fr-FR'))
/* the I-2 vet: a support's link is the lib's (supportUrl: staging-go. on staging), never one rebuilt by hand */
const linkOf = (s: SupportRef) => supportUrl(s.token)
const plural = (n: number, w: string) => `${n} ${w}${n > 1 ? 's' : ''}`

type Onglet = 'membres' | 'invitations' | 'caisses'
const ONGLETS: [string, string][] = [['membres', 'Membres'], ['invitations', 'Invitations'], ['caisses', 'Vos caisses'], ['defi', 'Défi collectif']]
/** The Rôle filter: the four login roles, and the member without a login (the app's « Ajouter un membre ») */
type RoleFilter = 'all' | 'none' | Role
type AccessFilter = 'all' | 'actif' | 'invite' | 'retire'
const SANS_CONNEXION = 'Sans connexion'

/* ---------- the invitations prepared in this browser (the demo; with an API the route is Technical's) ---------- */
interface Prepared { id: string; name: string; email: string; role: TeamRole; locations: string[]; at: string }
const preparedKey = () => `bmr.invitations.${currentWorld()}`
function readPrepared(): Prepared[] {
  try { const raw = localStorage.getItem(preparedKey()); if (raw) { const l = JSON.parse(raw) as unknown; if (Array.isArray(l)) return l as Prepared[] } } catch { /* fine */ }
  return []
}
function addPrepared(p: Prepared): void { try { localStorage.setItem(preparedKey(), JSON.stringify([...readPrepared(), p])) } catch { /* fine */ } }

/** One row of the Invitations tab, whatever its source (the account's open invitation, or one prepared here). */
interface InviteRow { id: string; who: string; email: string | null; role: TeamRole | null; meta: string; locations: string[]; manage: boolean }
const inviteRows = (pending: PendingRow[], prepared: Prepared[]): InviteRow[] => [
  ...pending.filter(r => r.kind === 'invitation').map(r => ({ id: r.id, who: r.title, email: null, role: r.role ?? null, meta: r.meta, locations: r.locations ?? [], manage: true })),
  ...prepared.map(p => ({ id: p.id, who: p.name || p.email, email: p.name ? p.email : null, role: p.role, meta: `invitation préparée le ${p.at} · envoi à venir`, locations: p.locations, manage: false })),
]

export default function Equipe() {
  const [params, setParams] = useSearchParams()
  const nav = useNavigate()
  const toast = useToast()
  const [scope] = useScope()
  const { data: places, isPending: placesPending } = useLocations()
  const { data: apiSupports } = useSupports()
  const { data: ledger, isLoading } = useMemberLedger()
  const { data: bill } = useBilling()
  const [, setTick] = useState(0)
  const [badgeFor, setBadgeFor] = useState<Member | null>(null)
  const [adding, setAdding] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [saving, setSaving] = useState(false)
  const { create: createSupport } = useSupportWrites()
  const [creating, setCreating] = useState(false)
  const [resetFor, setResetFor] = useState<Member | null>(null)
  /* WF-031 · search and filters */
  const [q, setQ] = useState('')
  const [roleF, setRoleF] = useState<RoleFilter>('all')
  const [accessF, setAccessF] = useState<AccessFilter>('all')
  /* WF-116 · the dialog */
  const [inviting, setInviting] = useState(false)
  const [invName, setInvName] = useState('')
  const [invEmail, setInvEmail] = useState('')
  const [invRole, setInvRole] = useState<TeamRole>('staff')

  if (isLoading || placesPending) return (
    <div style={{ display: 'grid', gap: 10, maxWidth: 720 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
  )

  const all = places ?? []
  const many = all.length > 1
  const placeName = (id: string) => all.find(l => l.id === id)?.name ?? id
  const home = scope !== ALL ? scope : all[0]?.id ?? 'l0'
  const [where, setWhere] = [params.get('lieu') ?? home, (v: string) => { const p = new URLSearchParams(params); p.set('lieu', v); setParams(p, { replace: true }) }]
  const onglet: Onglet = (['membres', 'invitations', 'caisses'] as const).find(o => o === params.get('onglet')) ?? 'membres'
  const setOnglet = (o: string) => {
    if (o === 'defi') { nav('/equipe/defi'); return }
    const p = new URLSearchParams(params); if (o === 'membres') p.delete('onglet'); else p.set('onglet', o); setParams(p)
  }

  const supports = mergeSupports(apiSupports)
  const members = getMembers().filter(m => scope === ALL || worksAt(m, scope))
  /* « Réinitialiser son code »: the owner, an admin, or a manager for a member of their establishments (08 §8.1) */
  const me = currentUser()
  const canManage = me.role !== 'staff'
  const canReset = (m: Member) => me.role === 'owner' || me.role === 'admin' || (me.role === 'manager' && m.locationId !== null)
  const rows: MemberRow[] = members.map(m => memberRow(m, supports, ledger))

  /* item 3 of the b-next-row ruling: with an API the personal QR is the server's (POST /v1/app/supports, its member set once) */
  const createQR = async (m: Member) => {
    if (creating) return
    setCreating(true)
    try {
      /* a member of the whole workspace gets it where the page is, else at the first establishment (default 5) */
      await createSupport({ name: `${m.firstName} — QR personnel`, kind: 'personnel', locationId: serverLocationId(m.locationId ?? home), memberId: m.id, methods: ['qr', 'web'] })
      setTick(t => t + 1)
      toast({ message: `Le QR de ${m.firstName} est prêt — chaque participation par ce QR lui est attribuée.` })
    } catch (e) {
      toast({ message: supportRefusal(e) })
    } finally { setCreating(false) }
  }
  const add = async () => {
    if (!firstName.trim() || saving) return
    setSaving(true)
    try {
      const m = await addMember(firstName, many ? where : home)
      setFirstName(''); setAdding(false); setTick(t => t + 1)
      toast({ message: `${m.firstName} fait partie de l’équipe. Créez son QR pour lui attribuer ses participations.` })
    } catch (e) {
      toast({ message: memberRefusal(e) })
    } finally { setSaving(false) }
  }
  const reset = async () => {
    const m = resetFor
    if (!m || saving) return
    setSaving(true)
    try {
      await resetMemberPin(m.id)
      setResetFor(null); setTick(t => t + 1)
      toast({ message: 'Code réinitialisé.' })
    } catch (e) {
      setResetFor(null)
      toast({ message: memberRefusal(e) })
    } finally { setSaving(false) }
  }
  /* WF-116 · « Préparer l’invitation »: the demo keeps it in this browser and lists it as « Invité »; with an API the route
     is Technical's — the button says so and sends nothing */
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invEmail.trim())
  const prepare = () => {
    if (!emailOk || apiWrites) return
    const at = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
    addPrepared({ id: `inv-${Date.now().toString(36)}`, name: invName.trim(), email: invEmail.trim(), role: invRole, locations: unscopedRole(invRole) ? [] : [many ? where : home], at })
    setInviting(false); setInvName(''); setInvEmail(''); setInvRole('staff'); setTick(t => t + 1)
    toast({ message: `Invitation préparée pour ${invName.trim() || invEmail.trim()} — elle est dans l’onglet Invitations.` })
    setOnglet('invitations')
  }
  const openSupport = (s: SupportRef) => nav(`/supports?vue=detail&id=${encodeURIComponent(s.id)}`)

  /* the record's reset dialog (V18), on the member's page */
  const resetDialog = (
    <Modal open={resetFor !== null} title={`Réinitialiser le code de ${resetFor?.firstName ?? ''} ?`} onClose={() => setResetFor(null)}
      actions={<>
        <Button variant="quiet" size="sm" onClick={() => setResetFor(null)}>Annuler</Button>
        <Button variant="primary" size="sm" disabled={saving} onClick={() => { void reset() }}>Réinitialiser</Button>
      </>}>
      <p className="t-body" style={{ margin: 0 }}>Son code actuel ne marchera plus. {resetFor?.firstName} en choisira un nouveau à la caisse, la prochaine fois.</p>
    </Modal>
  )

  /* ---------- « Gérer l’accès » — one member's own page (batch 22's « Moi ») ---------- */
  const moiId = params.get('membre')
  // roles ruling: a Collaborateur reads her own numbers only — a typed ?membre= URL falls back to the list (hide, never refuse)
  const moi = canManage && moiId ? rows.find(r => r.member.id === moiId) : undefined
  if (moi) {
    const m = moi.member
    return (
      <section data-frame="EQ-02" className="page" style={{ maxWidth: 760 }}>
        <p className="f-help" style={{ margin: 0 }}><Link to="/equipe">‹ Équipe et caisses</Link></p>
        <PageHeader title={`${m.firstName} — accès et QR`}
          description={`Ce que ${m.firstName} voit : son QR, son lien, ses chiffres — jamais ceux des autres.`}
          next={<Link to="/equipe/defi/moi">sa place dans le défi en cours</Link>} />
        <Card data-testid="member-code">
          <div style={{ display: 'grid', gap: 10 }}>
            <h2 className="t-section" style={{ margin: 0 }}>Code personnel</h2>
            <p className="t-body" style={{ margin: 0 }}>{m.pinSet
              ? `${m.firstName} a choisi ses 4 chiffres. Chaque validation est enregistrée à son nom.`
              : `${m.firstName} choisira ses 4 chiffres à la caisse, la première fois.`}</p>
            {m.pinSet && canReset(m) && <div><Button size="sm" variant="quiet" onClick={() => setResetFor(m)}>Réinitialiser son code</Button></div>}
          </div>
        </Card>
        {moi.support ? (
          <Card>
            <KV rows={[
              ['Son QR', <span key="q" style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><Pill tone="ok">Prêt</Pill> {moi.support.name} <Button size="sm" variant="quiet" onClick={() => openSupport(moi.support!)}>Voir sa carte QR</Button></span>],
              ['Son lien', <code key="l" style={{ overflowWrap: 'anywhere' }}>{linkOf(moi.support)}</code>],
              ['Accès', m.access === 'none' ? SANS_CONNEXION : `${ROLE_LABEL[m.access]} — ${ROLE_LINES[m.access]}`],
              ['Établissement', memberPlace(m, placeName)],
            ]} />
          </Card>
        ) : (
          <EmptyState title={`${m.firstName} n’a pas encore de QR.`}
            why={`Sans QR personnel, ${m.firstName} valide quand même les cadeaux à la caisse ; ses participations ne lui sont simplement pas attribuées.`}
            action={canManage ? <Button variant="primary" size="sm" disabled={creating} onClick={() => { void createQR(m) }}>Créer son QR</Button> : undefined} />
        )}
        <Tiles>
          <Tile label="Scans · 30 j" value={num(moi.scans)} />
          <Tile label="Participations" value={num(moi.participations)} />
          <Tile label="Cadeaux validés" value={num(moi.validated)} hint="à la caisse, avec son code" />
        </Tiles>
        <p className="f-help">{m.firstName} valide un cadeau à la caisse avec son code à 4 chiffres — <Link to="/caisse">ouvrir la caisse</Link>.</p>
        {resetDialog}
      </section>
    )
  }

  /* ---------- the filters (Membres and Invitations share them) ---------- */
  const needle = q.trim().toLocaleLowerCase('fr-FR')
  const matchQ = (...parts: (string | null | undefined)[]) => !needle || parts.some(p => (p ?? '').toLocaleLowerCase('fr-FR').includes(needle))
  const shownRows = rows.filter(r =>
    matchQ(r.member.firstName, r.member.access === 'none' ? SANS_CONNEXION : ROLE_LABEL[r.member.access], memberPlace(r.member, placeName))
    && (roleF === 'all' || r.member.access === roleF)
    && (accessF === 'all' || accessF === 'actif'))
  const pending = (bill?.teamEmpty ? [] : bill?.pending ?? []).filter(r => !r.role || actsIn(r.role, r.locations ?? [], scope, ALL))
  const invitations = inviteRows(pending, readPrepared().filter(p => !p.role || actsIn(p.role, p.locations, scope, ALL))).filter(r =>
    matchQ(r.who, r.email, r.role ? ROLE_LABEL[r.role] : null)
    && (roleF === 'all' || r.role === roleF)
    && (accessF === 'all' || accessF === 'invite'))
  const filters = (who: 'personne') => (
    <Card data-testid="equipe-filtres">
      <div className="fields-row" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' }}>
        <Field label={who === 'personne' ? 'Rechercher une personne' : 'Rechercher'}>{id => <Input id={id} value={q} placeholder="Nom, mot-clé…" onChange={e => setQ(e.target.value)} />}</Field>
        <Field label="Rôle">{id => (
          <Select id={id} value={roleF} onChange={e => setRoleF(e.target.value as RoleFilter)}>
            <option value="all">Tous les rôles</option>
            {ROLE_ORDER.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            <option value="none">{SANS_CONNEXION}</option>
          </Select>
        )}</Field>
        <Field label="Accès">{id => (
          <Select id={id} value={accessF} onChange={e => setAccessF(e.target.value as AccessFilter)}>
            <option value="all">Tous les accès</option>
            <option value="actif">Actif</option>
            <option value="invite">Invité</option>
            <option value="retire">Retiré</option>
          </Select>
        )}</Field>
      </div>
      <p className="f-help" style={{ margin: '8px 0 0' }} data-testid="equipe-resultats">{plural(onglet === 'invitations' ? invitations.length : shownRows.length, 'résultat')}</p>
    </Card>
  )

  const header = (
    <PageHeader title="Équipe et caisses" crumb="Votre établissement"
      description="Chacun utilise son accès et son code personnel, dans le même salon."
      actions={canManage ? <>
        <Button variant="primary" size="sm" onClick={() => setInviting(true)} data-testid="inviter-une-personne"><Icon name="plus" /> Inviter une personne</Button>
        <Button variant="quiet" size="sm" onClick={() => { setAdding(a => !a); setOnglet('membres') }}>Ajouter un membre</Button>
      </> : undefined}
      tabs={<Tabs value={onglet} onChange={setOnglet} options={ONGLETS} />} />
  )

  /* WF-116 · « Inviter une personne » — a dialog over the page, the capability sentence follows the chosen role */
  const inviteDialog = (
    <Modal open={inviting} title="Inviter une personne" onClose={() => setInviting(false)}
      actions={<>
        <Button variant="quiet" size="sm" onClick={() => setInviting(false)}>Annuler</Button>
        <Button variant="primary" size="sm" disabled={!emailOk || apiWrites} onClick={prepare} data-testid="preparer-invitation">Préparer l’invitation</Button>
      </>}>
      <div style={{ display: 'grid', gap: 12 }} data-testid="dialogue-inviter">
        <Field label="Prénom / nom">{id => <Input id={id} value={invName} maxLength={60} autoFocus onChange={e => setInvName(e.target.value)} />}</Field>
        <Field label="Adresse e-mail">{id => <Input id={id} type="email" value={invEmail} maxLength={120} onChange={e => setInvEmail(e.target.value)} />}</Field>
        <Field label="Rôle" help={ROLE_LINES[invRole]}>{id => (
          <Select id={id} value={invRole} onChange={e => setInvRole(e.target.value as TeamRole)}>
            {(['admin', 'manager', 'staff'] as TeamRole[]).map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </Select>
        )}</Field>
        {apiWrites
          ? <Note tone="candour">L’envoi des invitations passe par le service connecté — pas encore disponible ici. Rien n’est envoyé.</Note>
          : <p className="f-help" style={{ margin: 0 }}>L’invitation est préparée dans cet espace&nbsp;: elle apparaît dans l’onglet Invitations avec l’accès «&nbsp;Invité&nbsp;». Son code à 4 chiffres se choisira à la caisse, la première fois.</p>}
      </div>
    </Modal>
  )

  /* ---------- WF-032 · Invitations ---------- */
  if (onglet === 'invitations') return (
    <section data-frame="EQ-01" className="page">
      {header}
      {filters('personne')}
      {invitations.length === 0
        ? <EmptyState title="Aucune personne pour le moment." why="Les éléments enregistrés apparaîtront ici." />
        : (
          <div className="tablewrap">
            <table className="tbl" data-testid="invitations-table">
              <thead><tr><th>Personne</th><th>Rôle</th><th>Accès</th><th>Carte QR</th><th></th></tr></thead>
              <tbody>
                {invitations.map(r => (
                  <tr key={r.id}>
                    <td><strong>{r.who}</strong>{r.email && <><br /><span className="f-help">{r.email}</span></>}</td>
                    <td>{r.role ? ROLE_LABEL[r.role] : '—'}</td>
                    <td><Pill tone="warn">Invité</Pill></td>
                    <td><span className="f-help">après son arrivée</span></td>
                    <td>{r.manage && canManage && <Button size="sm" variant="quiet" onClick={() => nav('/parametres/equipe/gestion')}>Gérer</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <p className="f-help" style={{ margin: 0 }}>Les codes personnels ne sont jamais affichés dans la liste.</p>
      <Card data-testid="invitations-recuperation">
        <p className="t-section" style={{ marginTop: 0 }}>Invitations et récupération de code</p>
        {invitations.length === 0
          ? <p className="t-body" style={{ margin: 0 }}>Aucune invitation à reprendre.</p>
          : (
            <List>
              {invitations.map(r => <ListRow key={r.id} title={r.who} meta={r.meta} end={r.manage && canManage ? <Button size="sm" variant="quiet" onClick={() => nav('/parametres/equipe/gestion')}>Gérer</Button> : undefined} />)}
            </List>
          )}
        <p className="t-body" style={{ margin: '10px 0 0' }}>Un nouveau code retire immédiatement l’ancien accès caisse de cette personne. Les validations passées restent attribuées.</p>
        <p className="f-help" style={{ margin: '6px 0 0' }}>Perte de l’accès propriétaire&nbsp;: récupération d’identité par le service connecté. Aucun contournement du verrouillage de caisse.</p>
        <div style={{ marginTop: 12 }}><Button size="sm" onClick={() => nav('/caisse')}>Continuer à la caisse</Button></div>
      </Card>
      {inviteDialog}
    </section>
  )

  /* ---------- WF-033 · Vos caisses ---------- */
  if (onglet === 'caisses') return (
    <section data-frame="EQ-01" className="page">
      {header}
      <VosCaisses />
      {inviteDialog}
    </section>
  )

  /* ---------- WF-031 · Membres ---------- */
  const addCard = adding && canManage && (
    <Card>
      <div style={{ display: 'grid', gap: 12 }}>
        <div className="fields-row" style={{ gridTemplateColumns: many ? '1fr 1fr' : '1fr' }}>
          <Field label="Prénom">{id => <Input id={id} value={firstName} maxLength={40} autoFocus onChange={e => setFirstName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void add() }} />}</Field>
          {many && (
            <Field label="Établissement">{id => (
              <Select id={id} value={where} onChange={e => setWhere(e.target.value)}>
                {all.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
            )}</Field>
          )}
        </div>
        <p className="f-help" style={{ margin: 0 }}>Son code à 4 chiffres se choisit à la caisse, la première fois. Une connexion à l’espace se donne par «&nbsp;Inviter une personne&nbsp;» ou dans <Link to="/parametres/compte">Compte et accès</Link>.</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="primary" size="sm" disabled={!firstName.trim() || saving} onClick={() => { void add() }}>Ajouter</Button>
          <Button variant="quiet" size="sm" onClick={() => { setAdding(false); setFirstName('') }}>Annuler</Button>
        </div>
      </div>
    </Card>
  )

  /* day-one · nobody yet */
  if (rows.length === 0 && !adding) return (
    <section data-frame="EQ-01" className="page">
      {header}
      <EmptyState title="Les personnes qui valident les cadeaux."
        why="C’est vide parce que vous n’avez ajouté personne. Vous pouvez tout faire seul : vous validerez les cadeaux vous-même."
        action={canManage ? <Button variant="primary" size="sm" onClick={() => setAdding(true)}><Icon name="plus" /> Ajouter un membre</Button> : undefined}>
        <p>Un membre, c’est un prénom et un code à 4 chiffres pour la caisse. Avec son propre QR, chaque participation par ce QR lui est attribuée.</p>
      </EmptyState>
      {inviteDialog}
    </section>
  )

  const nPlaces = all.filter(l => l.status !== 'inactive').length
  return (
    <section data-frame="EQ-01" className="page">
      {header}
      {addCard}
      {filters('personne')}

      <div className="tablewrap">
        <table className="tbl" data-testid="membres-table">
          <thead><tr>
            <th>Personne</th>{many && scope === ALL && <th>Établissement</th>}<th>Rôle</th><th>Accès</th><th>Carte QR</th><th></th>
          </tr></thead>
          <tbody>
            {shownRows.map(r => (
              <tr key={r.member.id}>
                <td><strong>{r.member.firstName}</strong><br /><span className="f-help">code caisse {r.member.pinSet ? 'défini' : 'à choisir'}</span></td>
                {many && scope === ALL && <td>{memberPlace(r.member, placeName)}</td>}
                <td>{r.member.access === 'none' ? SANS_CONNEXION : ROLE_LABEL[r.member.access]}{accessLine(r.member) && <><br /><span className="f-help">{accessLine(r.member)}</span></>}</td>
                <td><Pill tone="ok">Actif</Pill></td>
                <td>{r.support
                  ? <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><Pill tone="ok">QR prêt</Pill>
                      <Button size="sm" variant="quiet" onClick={() => openSupport(r.support!)}>Voir sa carte QR</Button>
                      <Button size="sm" variant="ghost" onClick={() => setBadgeFor(r.member)}>Carte badge</Button></span>
                  : canManage ? <Button size="sm" variant="quiet" disabled={creating} onClick={() => { void createQR(r.member) }}>Créer son QR</Button> : <span className="f-help">pas encore de QR</span>}</td>
                <td>{r.member.access === 'owner'
                  ? <span className="f-help">Propriétaire protégé</span>
                  : canManage ? <Button size="sm" variant="ghost" onClick={() => nav(`/equipe?membre=${r.member.id}`)}>Gérer l’accès</Button> : null}</td>
              </tr>
            ))}
            {shownRows.length === 0 && <tr><td colSpan={6}><span className="f-help">Aucune personne pour le moment.</span></td></tr>}
          </tbody>
        </table>
      </div>
      <p className="f-help" style={{ margin: 0 }}>Les codes personnels ne sont jamais affichés dans la liste. Scans et participations viennent du QR personnel — sans QR, rien n’est attribué. Les cadeaux validés viennent de la caisse, avec le code de chacun. Le nombre de personnes n’est pas facturé.</p>

      <Card data-testid="qui-peut-faire-quoi">
        <p className="t-section" style={{ marginTop: 0 }}>Qui peut faire quoi&nbsp;?</p>
        {nPlaces > 1
          ? <p className="f-help" style={{ margin: 0 }}>Propriétaire et Administrateur voient tout l’espace&nbsp;; Manager et Collaborateur ne voient que les établissements qui leur sont attribués.</p>
          : null}
        <div style={{ display: 'grid', gap: 6 }}>
          {ROLE_ORDER.map(r => <p key={r} className="t-body" style={{ margin: 0 }}><b>{ROLE_LABEL[r]}</b> · {ROLE_LINES[r]}</p>)}
        </div>
        <p className="t-section" style={{ margin: '16px 0 8px', fontSize: 17 }}>Supports attribués</p>
        <List>
          {rows.map(r => (
            <ListRow key={r.member.id} title={`${r.member.firstName} · ${plural(r.support ? 1 : 0, 'support')}`}
              meta={r.support ? r.support.name : 'aucun QR personnel'}
              end={r.support
                ? <Button size="sm" variant="quiet" onClick={() => openSupport(r.support!)}>Voir sa carte QR</Button>
                : canManage ? <Button size="sm" variant="quiet" disabled={creating} onClick={() => { void createQR(r.member) }}>Créer un support</Button> : undefined} />
          ))}
        </List>
      </Card>

      <Modal open={badgeFor !== null} title="Carte badge" onClose={() => setBadgeFor(null)}
        actions={<Button variant="primary" size="sm" onClick={() => setBadgeFor(null)}>Compris</Button>}>
        <Note tone="candour">La carte badge en PDF n’est pas encore prête.</Note>
        <p className="t-body">Elle portera le QR de {badgeFor?.firstName} et « Scannez mon code », prête à imprimer. Son QR fonctionne déjà&nbsp;: <Link to="/supports" onClick={() => setBadgeFor(null)}>le voir dans Supports</Link> — et {badgeFor?.firstName} le retrouve dans «&nbsp;Mon QR&nbsp;», avec sa carte PNG.</p>
      </Modal>
      {inviteDialog}
    </section>
  )
}

/** owner / admin see the whole workspace (0018): an invitation for them carries no establishment */
const unscopedRole = (r: TeamRole) => r === 'owner' || r === 'admin'
